import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { getColor } from '../../core/themev3';
import { select } from '../../core/themev2';
import { getSiteName, getSiteDomain } from '../../helpers';
import { sendCustomTrackingInfo } from '../../index';

const UnreadLeadsModal = ({
  device,
  onClose,
  publisherTypeId,
  publisherId,
  UnreadLeadsMsgOperations,
  UnreadLeadsMsgSelectors,
  onlinePostingsList,
  comesFromArchive,
  postings
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isProfessional = publisherTypeId !== '1';
  const [active, setActive] = useState(false);
  const [unansweredMessage, setUnansweredMessage] = useState(
    t(`panel.avisos.UnreadLeads.default_message${!isProfessional ? '.particular' : ''}`, {
      siteName: getSiteName(),
      siteDomain: getSiteDomain(),
      publisherId: [publisherId]
    })
  );
  const { postingIds, postingIdsToEnd } = useSelector(UnreadLeadsMsgSelectors);
  const isPlural = postingIds && postingIds.length > 1;
  const onChangeText = e => setUnansweredMessage(e.target.value);

  useEffect(() => {
    // Verify if the publisherId from the postings to end is different from the current user logged publisherId
    if (postings && postingIdsToEnd) {
      const publisherIdFound = postings?.find(
        p => postingIdsToEnd?.toString()?.split(',')[0] === p.postingId?.toString()
      )?.publisherId;
      if (publisherIdFound && publisherIdFound.toString() !== publisherId) {
        setUnansweredMessage(
          t(`panel.avisos.UnreadLeads.default_message${!isProfessional ? '.particular' : ''}`, {
            siteName: getSiteName(),
            siteDomain: getSiteDomain(),
            publisherId: [publisherIdFound]
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnClick = () => {
    dispatch(
      UnreadLeadsMsgOperations(
        onlinePostingsList && onlinePostingsList.length > 0 ? onlinePostingsList : postingIds,
        unansweredMessage
      )
    );
    onClose();
    sendCustomTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Panel',
      eventAction: 'Mis avisos',
      eventLabel: `${comesFromArchive ? 'Archivar' : 'Finalizar'} Aviso-enviar`
    });
  };

  return (
    <Container device={device}>
      <Title data-panel='panel-modal-UnreadLeads-title'>
        {t(`panel.avisos.UnreadLeads.title${isPlural ? '.plural' : ''}`, { value: postingIds && postingIds.length })}
      </Title>
      <Description data-panel='panel-modal-UnreadLeads-description'>
        {t(`panel.avisos.UnreadLeads.subtitle${isPlural ? '.plural' : ''}`)}
      </Description>
      <TextArea
        maxLength={539}
        placeholder={t('panel.avisos.UnreadLeads.placeholder')}
        rows={10}
        onChange={onChangeText}
        data-panel='panel-modal-UnreadLeads-textArea'
        value={unansweredMessage}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        activeBorder={active}
      />
      <CTA onClick={handleOnClick} data-panel='panel-modal-UnreadLeads-buttonCTA'>
        {t('panel.avisos.UnreadLeads.cta')}
      </CTA>
    </Container>
  );
};

const Container = styled.div`
  padding: ${({ device }) => (device === 'mobile' ? '28px' : '20px 24px')};
  max-width: 507px;
`;

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  white-space: break-spaces;
  margin-bottom: 22px;
`;

const Description = styled.div`
  margin-bottom: 22px;
  font-family: Hind;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0em;
  text-align: center;
  white-space: break-spaces;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 226px;
  margin-bottom: 40px;
  overflow: auto;
  resize: none;
  border: 1px solid ${select('grayMediumLight')};
  border-radius: 8px;
  padding: 12px;
  font-family: Hind;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0em;
  text-align: left;
  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: ${select('grayMediumDark')};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${select('grayMediumDark')};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: ${select('grayMediumDark')};
    font-family: Hind;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }

  ${({ activeBorder }) =>
    activeBorder &&
    css`
      border-color: ${getColor('primary')};
    `}
`;

const CTA = styled.button`
  height: 48px;
  width: 100%;
  padding: 0px 12px;
  border-radius: 8px;
  color: white;
  background-color: ${getColor('primary')};
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  :hover {
    background-color: ${getColor('secondary')};
  }
`;

export default UnreadLeadsModal;
